// src/components/ClientSection.js (New "Directory View" with Correct Columns)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFirebase } from '../App';
import { collection, query, orderBy, getDocs, doc, updateDoc, addDoc, Timestamp, limit, startAfter, endBefore, limitToLast, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { TableEmptyRow, TableLoadingRow } from './Common/PanelState';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

// --- Internal Component: ClientForm (Remains the same) ---
function ClientForm({ onBack, clientToEdit }) {
    const { db } = useFirebase();
    const [formData, setFormData] = useState({ name: '', companyName: '', contact: '', email: '', companyAddress: '', cnic: '', ntn: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => { 
        if (clientToEdit) { setFormData(clientToEdit); }
    }, [clientToEdit]);
    
    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const { id, ...dataToSave } = { ...formData, updatedAt: Timestamp.now() };
            if (clientToEdit) {
                await updateDoc(doc(db, 'clients', clientToEdit.id), dataToSave);
                toast.success("Client updated!");
            } else {
                dataToSave.createdAt = Timestamp.now();
                await addDoc(collection(db, 'clients'), dataToSave);
                toast.success("Client added!");
            }
            onBack();
        } catch (error) { toast.error(`Failed to save client: ${error.message}`); } 
        finally { setIsSubmitting(false); }
    };

    return (
        <div className="container mx-auto">
             <div className="mb-8">
                <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800">
                    <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" /> Back to Client List
                </button>
                <h1 className="admin-page-title mt-2">{clientToEdit ? 'Edit Ad Client' : 'Add New Ad Client'}</h1>
            </div>
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-md space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label htmlFor="name" className="block text-sm font-medium text-gray-700">Client Name <span className="text-red-500">*</span></label><input type="text" id="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                    <div><label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name <span className="text-red-500">*</span></label><input type="text" id="companyName" value={formData.companyName} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                    <div><label htmlFor="contact" className="block text-sm font-medium text-gray-700">Contact <span className="text-red-500">*</span></label><input type="tel" id="contact" value={formData.contact} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                    <div><label htmlFor="email" className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label><input type="email" id="email" value={formData.email} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required/></div>
                    <div><label htmlFor="cnic" className="block text-sm font-medium text-gray-700">CNIC <span className="text-red-500">*</span></label><input type="text" id="cnic" value={formData.cnic} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                    <div><label htmlFor="ntn" className="block text-sm font-medium text-gray-700">NTN (Optional)</label><input type="text" id="ntn" value={formData.ntn} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" /></div>
                    <div className="md:col-span-2"><label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700">Company Address <span className="text-red-500">*</span></label><textarea id="companyAddress" value={formData.companyAddress} onChange={handleChange} rows="3" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                </div>
                <div className="flex justify-end pt-6 border-t">
                    <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto text-white font-bold py-3 px-10 rounded-lg shadow-lg transition-transform hover:scale-105" style={{ backgroundColor: isSubmitting ? '#a11217' : '#c4151c' }}>
                        {isSubmitting ? 'Saving...' : (clientToEdit ? 'Save Changes' : 'Add Client')}
                    </button>
                </div>
            </form>
        </div>
    );
}

// --- Main Component: ClientSection ---
function ClientSection() {
    const [view, setView] = useState('list');
    const [editingClient, setEditingClient] = useState(null);
    const { db } = useFirebase();
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClients, setTotalClients] = useState(0);
    const [lastVisible, setLastVisible] = useState(null);
    const [firstVisible, setFirstVisible] = useState(null);

    const getInitials = (name) => {
        if (!name) return '?';
        const words = name.split(' ');
        if (words.length > 1 && words[1]) return `${words[0][0]}${words[1][0]}`.toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    const fetchClients = useCallback(async (direction = 'first') => {
        if (!db) { setClients([]); setTotalClients(0); setIsLoading(false); return; }
        setIsLoading(true);
        const clientsRef = collection(db, 'clients');
        if (direction === 'first') {
            const countSnapshot = await getDocs(query(clientsRef));
            setTotalClients(countSnapshot.size);
        }
        let finalQuery;
        if (direction === 'next' && lastVisible) {
            finalQuery = query(clientsRef, orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(pageSize));
        } else if (direction === 'prev' && firstVisible) {
            finalQuery = query(clientsRef, orderBy('createdAt', 'desc'), endBefore(firstVisible), limitToLast(pageSize));
        } else {
            finalQuery = query(clientsRef, orderBy('createdAt', 'desc'), limit(pageSize));
        }
        const docSnap = await getDocs(finalQuery);
        setClients(docSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        if (!docSnap.empty) {
            setFirstVisible(docSnap.docs[0]);
            setLastVisible(docSnap.docs[docSnap.docs.length - 1]);
        } else {
            setFirstVisible(null); setLastVisible(null);
        }
        setIsLoading(false);
    }, [db, pageSize, lastVisible, firstVisible]);

    useEffect(() => {
        setCurrentPage(1); setLastVisible(null); setFirstVisible(null); fetchClients('first');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);
    
    const filteredClients = clients.filter(c => 
        (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.companyName && c.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const handleNextPage = () => { if (lastVisible) { setCurrentPage(p => p + 1); fetchClients('next'); } };
    const handlePrevPage = () => { if (currentPage > 1) { setCurrentPage(p => p - 1); fetchClients('prev'); } };
    const handleEdit = (client) => { setEditingClient(client); setView('form'); }
    const handleAddNew = () => { setEditingClient(null); setView('form'); }
    
    if (view === 'form') {
        return <ClientForm onBack={() => { setView('list'); fetchClients('first'); }} clientToEdit={editingClient} />;
    }
    
    const pageStart = totalClients > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalClients);

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div><h1 className="admin-page-title">Ad Clients</h1><p className="admin-page-sub">Manage your list of advertisement clients.</p></div>
                <button onClick={handleAddNew} className="text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-transform hover:scale-105 flex items-center gap-2" style={{backgroundColor: '#c4151c'}}>
                    <Icon path="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <span>Add New Client</span>
                </button>
            </div>
            
            <div className="mb-6"><input type="text" placeholder="Search by client or company name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-white border-gray-300 rounded-lg shadow-sm p-4" /></div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                            {/* --- CORRECTED TABLE HEADERS --- */}
                            <tr>
                                <th className="px-6 py-3 text-left">Client / Company</th>
                                <th className="px-6 py-3 text-left hidden md:table-cell">Contact Info</th>
                                <th className="px-6 py-3 text-left">CNIC / NTN</th>
                                <th className="px-6 py-3 text-left hidden lg:table-cell">Address</th>
                                <th className="px-6 py-3 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {isLoading ? (<TableLoadingRow colSpan={5} label="Loading clients" />) 
                        : filteredClients.length > 0 ? filteredClients.map(client => (
                            <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 font-bold">
                                            {getInitials(client.name)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-gray-800">{client.name}</div>
                                            <div className="text-sm text-gray-500">{client.companyName}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 hidden md:table-cell">
                                    <div className="text-sm text-gray-800">{client.contact}</div>
                                    <div className="text-xs text-gray-500">{client.email}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm text-gray-700 font-mono">{client.cnic}</div>
                                    <div className="text-xs text-gray-500">NTN: {client.ntn || 'N/A'}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 hidden lg:table-cell max-w-xs truncate">{client.companyAddress}</td>
                                <td className="px-6 py-4 text-center">
                                    <button onClick={() => handleEdit(client)} className="font-semibold text-sm bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-red-600 hover:text-white transition-colors">
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        )) : (<TableEmptyRow colSpan={5} title="No clients found" hint="Add a client to start billing." />)}
                        </tbody>
                    </table>
                </div>
                
                {totalClients > pageSize && (
                    <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 rounded-b-xl border-t">
                        <span className="text-sm font-medium text-gray-700">Showing {pageStart}-{pageEnd} of {totalClients}</span>
                        <div className="flex gap-2">
                            <button onClick={handlePrevPage} disabled={currentPage === 1 || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Previous</button>
                            <button onClick={handleNextPage} disabled={!lastVisible || pageEnd >= totalClients || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClientSection;